import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Trash2, 
  RefreshCw, 
  HardDrive, 
  Wifi, 
  WifiOff,
  AlertCircle,
  CheckCircle,
  Loader
} from 'lucide-react'
import { useCache, formatFileSize, useNetworkStatus } from '../../hooks/useCache'

const CacheManager: React.FC = () => {
  const { caches, isLoading, error, clearCache, clearAllCaches, refreshCaches } = useCache()
  const { isOnline, connectionType } = useNetworkStatus()
  const [selectedCaches, setSelectedCaches] = useState<string[]>([])

  const handleSelectCache = (cacheName: string) => {
    setSelectedCaches(prev => 
      prev.includes(cacheName) 
        ? prev.filter(name => name !== cacheName)
        : [...prev, cacheName]
    )
  }

  const handleSelectAll = () => {
    if (selectedCaches.length === caches.length) {
      setSelectedCaches([])
    } else {
      setSelectedCaches(caches.map(cache => cache.name))
    }
  }

  const handleClearSelected = async () => {
    for (const cacheName of selectedCaches) {
      await clearCache(cacheName)
    }
    setSelectedCaches([])
  }

  const getTotalSize = () => {
    return caches.reduce((total, cache) => total + cache.size, 0)
  }

  const getTotalEntries = () => {
    return caches.reduce((total, cache) => total + cache.entries, 0)
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
            <HardDrive className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              缓存管理
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              管理应用缓存和存储空间
            </p>
          </div>
        </div>
        <button
          onClick={refreshCaches}
          disabled={isLoading}
          className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 text-gray-600 dark:text-gray-400 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* 网络状态 */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {isOnline ? (
              <Wifi className="w-5 h-5 text-green-600" />
            ) : (
              <WifiOff className="w-5 h-5 text-red-600" />
            )}
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {isOnline ? '在线' : '离线'}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                连接类型: {connectionType}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {formatFileSize(getTotalSize())}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {getTotalEntries()} 个条目
            </p>
          </div>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
        >
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </motion.div>
      )}

      {/* 批量操作 */}
      {caches.length > 0 && (
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={selectedCaches.length === caches.length}
                onChange={handleSelectAll}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                全选 ({selectedCaches.length}/{caches.length})
              </span>
            </label>
          </div>
          {selectedCaches.length > 0 && (
            <button
              onClick={handleClearSelected}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 flex items-center space-x-2"
            >
              <Trash2 className="w-4 h-4" />
              <span>删除选中 ({selectedCaches.length})</span>
            </button>
          )}
        </div>
      )}

      {/* 缓存列表 */}
      <div className="space-y-3">
        {isLoading && caches.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600 dark:text-gray-400">加载中...</span>
          </div>
        ) : caches.length === 0 ? (
          <div className="text-center py-8">
            <HardDrive className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">暂无缓存数据</p>
          </div>
        ) : (
          caches.map((cache, index) => (
            <motion.div
              key={cache.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className={`p-4 rounded-lg border transition-all duration-200 ${
                selectedCaches.includes(cache.name)
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                  : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedCaches.includes(cache.name)}
                    onChange={() => handleSelectCache(cache.name)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {cache.name}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {formatFileSize(cache.size)} • {cache.entries} 个条目
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => clearCache(cache.name)}
                  disabled={isLoading}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* 底部操作 */}
      {caches.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              总计: {formatFileSize(getTotalSize())} • {getTotalEntries()} 个条目
            </div>
            <button
              onClick={clearAllCaches}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 flex items-center space-x-2"
            >
              <Trash2 className="w-4 h-4" />
              <span>清空所有缓存</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default CacheManager